import { Text, View } from 'react-native';
import { formatWeight, type SetProposal } from '@heraion/shared';

import { formatRelativeAge } from '../formatRelativeAge';
import { weightUnitLabels } from '../weightUnitLabels';
import type { useTheme } from './theme';
import type { WeightUnit } from './state/loggerDraft';
import { HistoryCopyRow } from './_HistoryCopyRow';
import type { HistoryPanelStyles } from './HistoryPanel.styles';

// The concrete sets worked out from the athlete's own last sessions on this
// exercise (EQ-2), shown under the computed weights in section 1. One caption
// says where they come from and how old that is; each row is a set to copy.
// Every proposal of one list shares its source and its anchor, so the caption
// is read once off the first.
export function ProposalRows({
  proposals,
  onCopySet,
  getExerciseNameBySlug,
  copyLabel,
  locale,
  unit,
  styles,
  theme,
  t,
}: {
  proposals: SetProposal[];
  /** Absent = no set is open, rows are information only. */
  onCopySet?: (patch: { weightKg: number; reps: number }) => void;
  getExerciseNameBySlug: (slug: string, fallback?: string) => string;
  copyLabel: string;
  locale: string;
  unit: WeightUnit;
  styles: HistoryPanelStyles;
  theme: ReturnType<typeof useTheme>;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const first = proposals[0];
  if (!first) return null;
  const unitLabels = weightUnitLabels(t);
  const age = formatRelativeAge(t, first.ageDays);
  const source = t('mobile.logger.historyProposalSource', {
    defaultValue: 'From your last {{count}} sessions of {{name}}',
    count: first.source.sessionCount,
    name: getExerciseNameBySlug(first.source.slug),
  });
  const axisLabel: Record<SetProposal['axis'], string> = {
    reps: t('mobile.logger.historyProposalAxisReps', {
      defaultValue: 'More reps, same weight',
    }),
    load: t('mobile.logger.historyProposalAxisLoad', {
      defaultValue: 'More weight, same reps',
    }),
    trade: t('mobile.logger.historyProposalAxisTrade', {
      defaultValue: 'Trade reps for weight',
    }),
  };
  const copySetA11y = t('mobile.logger.historyCopySetA11y', {
    defaultValue: "Copy this set onto the set you're on",
  });
  return (
    <>
      <View style={styles.proposalCaption}>
        <Text style={styles.candidateLabel}>
          {age ? `${source} · ${age}` : source}
        </Text>
      </View>
      {proposals.map((p) => {
        const rowText = `${p.reps} × ${formatWeight(p.weightKg, unit, locale, unitLabels)}`;
        return (
          <HistoryCopyRow
            key={p.axis}
            styles={styles}
            theme={theme}
            copyLabel={copyLabel}
            copyA11yLabel={`${copySetA11y}: ${axisLabel[p.axis]}, ${rowText}`}
            onCopy={
              onCopySet
                ? () => onCopySet({ weightKg: p.weightKg, reps: p.reps })
                : undefined
            }
          >
            <Text style={styles.candidateWeight}>{rowText}</Text>
            <Text style={styles.candidateLabel}>{axisLabel[p.axis]}</Text>
          </HistoryCopyRow>
        );
      })}
    </>
  );
}
